import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';

// Key Dates — READ-ONLY mirror of app-magic's Key Sell Dates (dbo.KeySellDates).
// The source of truth + all editing live in app-magic (magic.majans.com/sell-dates,
// role-gated + ChangeLog-audited + SignalR live-sync). This page only displays the
// customer sell-in milestone calendar: Engagement → Decision → DC → POG (plus the
// occasional Range Review). Data comes from /api/key-dates, which reads
// dbo.KeySellDates directly (same `magic` db). No MAGIC.xlsm, no local seed.

const MAGIC_SELL_DATES_URL = 'https://magic.majans.com/sell-dates';

const CUSTOMER_COLORS = {
  Woolworths: '#1a8a3f', Coles: '#e03131', Metcash: '#f59e0b', Costco: '#0ea5e9',
  Countdown: '#16a34a', 'Foodstuffs NI': '#8b5cf6', Spar: '#ec4899',
};
const customerColor = (c) => CUSTOMER_COLORS[c] || '#64748b';

const TYPE_BADGE = {
  Major: { bg: 'color-mix(in srgb, #8b5cf6 16%, transparent)', fg: '#a78bfa' },
  Minor: { bg: 'color-mix(in srgb, #3b82f6 16%, transparent)', fg: '#60a5fa' },
  Other: { bg: 'var(--border)', fg: 'var(--text-muted)' },
};
const STATUS_BADGE = {
  Assumption: { bg: 'var(--border)', fg: 'var(--text-muted)' },
  Published: { bg: 'var(--info-soft)', fg: 'var(--info)' },
  Confirmed: { bg: 'var(--success-soft)', fg: 'var(--success)' },
  Completed: { bg: 'var(--success-soft)', fg: 'var(--success)' },
  Cancelled: { bg: 'color-mix(in srgb, var(--danger) 14%, transparent)', fg: 'var(--danger)' },
};

const MILESTONES = [
  ['engagementDate', 'Engagement'], ['decisionDate', 'Decision'],
  ['dcDate', 'DC'], ['pogDate', 'POG'], ['rangeReviewDate', 'Range Review'],
];

// ── date helpers (browser-local, midnight-anchored) ──────────────────────────
const today = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
const parseISO = (s) => { if (!s) return null; const d = new Date(s + 'T00:00:00'); return isNaN(d) ? null : d; };
const daysFrom = (s) => { const d = parseISO(s); return d ? Math.round((d - today) / 86400000) : null; };
const fyFromPog = (r) => { const d = parseISO(r.pogDate); if (!d) return r.year; return (d.getMonth() + 1) >= 7 ? d.getFullYear() + 1 : d.getFullYear(); };
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (s) => { const d = parseISO(s); return d ? `${String(d.getDate()).padStart(2, '0')} ${MON[d.getMonth()]} ${String(d.getFullYear()).slice(2)}` : null; };
const dateColor = (s) => { const n = daysFrom(s); if (n == null) return 'var(--text-muted)'; if (n < 7) return 'var(--danger)'; if (n <= 30) return 'var(--warn)'; return 'var(--text-body)'; };

const quarterStart = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const quarterEnd = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3 + 3, 0);

const CUSTOMER_ORDER = ['Woolworths', 'Coles', 'Metcash', 'Costco', 'Countdown', 'Foodstuffs NI', 'Spar'];
const custRank = (c) => { const i = CUSTOMER_ORDER.indexOf(c); return i === -1 ? 99 : i; };

export default function KeyDates() {
  const apiFetch = useApi();
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['key-dates'],
    queryFn: () => apiFetch('/key-dates').then((r) => {
      if (!r.ok) throw new Error(`Failed to load key dates (HTTP ${r.status})`);
      return r.json();
    }),
    staleTime: 5 * 60 * 1000,
  });

  const [search, setSearch] = useState('');
  const [customer, setCustomer] = useState('All');
  const [fy, setFy] = useState('All');
  const [status, setStatus] = useState('All');
  const [channel, setChannel] = useState('All');
  const [hidePastPog, setHidePastPog] = useState(true);
  const [sortCol, setSortCol] = useState('pogDate');
  const [sortDir, setSortDir] = useState('asc');

  const withFy = useMemo(() => rows.map((r) => ({ ...r, fy: fyFromPog(r) })), [rows]);

  const customers = useMemo(() => ['All', ...Array.from(new Set(withFy.map((r) => r.customer).filter(Boolean))).sort((a, b) => custRank(a) - custRank(b))], [withFy]);
  const fys = useMemo(() => ['All', ...Array.from(new Set(withFy.map((r) => r.fy).filter(Boolean))).sort((a, b) => b - a)], [withFy]);
  const statuses = useMemo(() => ['All', ...Array.from(new Set(withFy.map((r) => r.status).filter(Boolean)))], [withFy]);
  const channels = useMemo(() => ['All', ...Array.from(new Set(withFy.map((r) => r.channel).filter(Boolean))).sort()], [withFy]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return withFy.filter((r) => {
      if (customer !== 'All' && r.customer !== customer) return false;
      if (fy !== 'All' && r.fy !== fy) return false;
      if (status !== 'All' && r.status !== status) return false;
      if (channel !== 'All' && r.channel !== channel) return false;
      if (hidePastPog && r.pogDate && daysFrom(r.pogDate) < 0) return false;
      if (q && !`${r.customer} ${r.channel} ${r.subCategory}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [withFy, search, customer, fy, status, channel, hidePastPog]);

  const summary = useMemo(() => ({
    total: filtered.length,
    upcoming: filtered.filter((r) => { const n = daysFrom(r.engagementDate); return n != null && n >= 0 && n <= 30; }).length,
    confirmed: filtered.filter((r) => r.status === 'Confirmed').length,
    customers: new Set(filtered.map((r) => r.customer)).size,
  }), [filtered]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av = a[sortCol]; let bv = b[sortCol];
      if (av == null) return 1; if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir(col.endsWith('Date') ? 'asc' : 'asc'); }
  };
  const SortIcon = ({ col }) => (sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const PILLS = (val, set, opts) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {opts.map((o) => (
        <button key={o} className={val === o ? 'pill-active' : 'pill-inactive'} style={{ fontSize: 11 }} onClick={() => set(o)}>
          {o === 'All' ? 'All' : o}
        </button>
      ))}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, margin: 0 }}>Key Dates</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            Customer sell-in milestones — Engagement → Decision → DC → POG. Mirrors{' '}
            <a href={MAGIC_SELL_DATES_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--ai)', textDecoration: 'underline', textDecorationColor: 'var(--text-muted)' }}>MAGIC Key Sell Dates</a>{' '}(read-only here).
          </div>
        </div>
        <a href={MAGIC_SELL_DATES_URL} target="_blank" rel="noreferrer"
          style={{ fontSize: 12, fontWeight: 600, color: 'var(--info)', background: 'var(--info-soft)', border: '1px solid var(--info-border)', borderRadius: 'var(--radius-md)', padding: '7px 12px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
          Manage in MAGIC →
        </a>
      </div>

      {isLoading && <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 24 }}>Loading key sell dates…</div>}
      {error && <div style={{ color: 'var(--danger)', fontSize: 13, padding: 16 }}>{String(error.message ?? error)}</div>}

      {!isLoading && !error && (
        <>
          {/* ── Summary cards ── */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Sell Dates', value: summary.total, color: 'var(--text-primary)', sub: hidePastPog ? 'upcoming (POG ≥ today)' : 'all' },
              { label: 'Upcoming Engagement', value: summary.upcoming, color: '#f59e0b', sub: 'next 30 days' },
              { label: 'Confirmed', value: summary.confirmed, color: '#22c55e', sub: 'retailer agreed' },
              { label: 'Customers', value: summary.customers, color: '#3b82f6', sub: 'in view' },
            ].map((k) => (
              <div key={k.label} className="kpi-card" style={{ flex: 1, minWidth: 150 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* ── Filters ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer / channel / category…"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-soft)', borderRadius: 'var(--radius)', color: 'var(--text-primary)', fontSize: 12, padding: '6px 10px', minWidth: 240 }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={hidePastPog} onChange={(e) => setHidePastPog(e.target.checked)} />
                Hide past POG
              </label>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <Group label="Customer">{PILLS(customer, setCustomer, customers)}</Group>
              <Group label="FY">{PILLS(fy, setFy, fys)}</Group>
              <Group label="Status">{PILLS(status, setStatus, statuses)}</Group>
              <Group label="Channel">{PILLS(channel, setChannel, channels)}</Group>
            </div>
          </div>

          {/* ── Timeline (Gantt) ── */}
          <Timeline records={filtered} />

          {/* ── Milestone table (app-magic mirror) ── */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>Sell-In Milestones</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{filtered.length} records · click a header to sort</div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <Th col="customer" {...{ sortCol, sortDir, handleSort }}>Customer</Th>
                    <th style={{ fontSize: 11 }}>Channel</th>
                    <Th col="year" {...{ sortCol, sortDir, handleSort }}>Year</Th>
                    <th style={{ fontSize: 11 }}>Type</th>
                    <th style={{ fontSize: 11 }}>Sub-Category</th>
                    <th style={{ fontSize: 11 }}>Status</th>
                    {MILESTONES.map(([key, label]) => (
                      <Th key={key} col={key} {...{ sortCol, sortDir, handleSort }} align="right">{label}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => {
                    const tb = TYPE_BADGE[r.type] || TYPE_BADGE.Other;
                    const sb = STATUS_BADGE[r.status] || STATUS_BADGE.Assumption;
                    return (
                      <tr key={r.id}>
                        <td style={{ fontSize: 12, fontWeight: 600, color: customerColor(r.customer), whiteSpace: 'nowrap' }}>{r.customer}</td>
                        <td style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{r.channel}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-body)', fontVariantNumeric: 'tabular-nums' }}>{r.year}</td>
                        <td><Badge {...tb}>{r.type}</Badge></td>
                        <td style={{ fontSize: 12, color: 'var(--text-primary)', maxWidth: 220 }}>{r.subCategory}</td>
                        <td><Badge {...sb}>{r.status}</Badge></td>
                        {MILESTONES.map(([key]) => (
                          <td key={key} style={{ textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: dateColor(r[key]), fontWeight: r[key] && daysFrom(r[key]) < 7 ? 600 : 400 }}>
                            {fmtDate(r[key]) ?? (key === 'decisionDate' ? <span style={{ color: 'var(--text-muted)' }}>Pending</span> : '—')}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '16px 0' }}>No sell dates match the current filters.</div>}
          </div>

          {/* ── Footer ── */}
          <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
            Source: <strong style={{ color: 'var(--text-body)' }}>dbo.KeySellDates</strong> in the shared <code>magic</code> database — owned and edited in{' '}
            <a href={MAGIC_SELL_DATES_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--ai)' }}>MAGIC → Key Sell Dates</a>.
            This view is read-only. FY derived from POG date (Jul–Jun).
          </div>
        </>
      )}
    </div>
  );
}

// ── small presentational helpers ─────────────────────────────────────────────
function Group({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      {children}
    </div>
  );
}

function Badge({ bg, fg, children }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 600, color: fg, background: bg, borderRadius: 4, padding: '2px 7px', whiteSpace: 'nowrap' }}>{children}</span>
  );
}

function Th({ col, sortCol, sortDir, handleSort, align, children }) {
  return (
    <th style={{ fontSize: 11, textAlign: align || 'left', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort(col)}>
      {children}{sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );
}

// ── Timeline (dynamic multi-year Gantt of Engagement → POG windows) ──────────
function Timeline({ records }) {
  const dated = useMemo(
    () => records.filter((r) => r.engagementDate && r.pogDate)
      .sort((a, b) => custRank(a.customer) - custRank(b.customer) || String(a.engagementDate).localeCompare(String(b.engagementDate))),
    [records],
  );

  const axis = useMemo(() => {
    if (!dated.length) return null;
    let min = parseISO(dated[0].engagementDate); let max = parseISO(dated[0].pogDate);
    for (const r of dated) {
      const e = parseISO(r.engagementDate); const p = parseISO(r.pogDate);
      if (e < min) min = e; if (p > max) max = p;
    }
    const start = quarterStart(min); const end = quarterEnd(max);
    const span = end - start;
    const ticks = [];
    let t = new Date(start);
    while (t <= end) {
      ticks.push({ date: new Date(t), label: `Q${Math.floor(t.getMonth() / 3) + 1} '${String(t.getFullYear()).slice(2)}` });
      t = new Date(t.getFullYear(), t.getMonth() + 3, 1);
    }
    const pct = (iso) => { const d = parseISO(iso); if (!d) return null; return Math.max(0, Math.min(100, ((d - start) / span) * 100)); };
    const todayPct = today >= start && today <= end ? ((today - start) / span) * 100 : null;
    return { ticks, pct, todayPct, tickW: 100 / ticks.length };
  }, [dated]);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>Sell-In Timeline</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 10, color: 'var(--text-muted)' }}>
          <Legend dot="#64748b" text="Engagement → POG window" />
          <Legend marker text="Decision" /><Legend marker dark text="DC" />
        </div>
      </div>

      {!dated.length ? (
        <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No dated sell-in windows in the current filters.</div>
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {/* Quarter axis header */}
          <div style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg-elevated)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
            <div style={{ width: 230, minWidth: 230, borderRight: '1px solid var(--border)' }} />
            <div style={{ position: 'relative', flex: 1, height: 28 }}>
              {axis.ticks.map((tk, i) => (
                <div key={i} style={{ position: 'absolute', left: `${axis.pct(tk.date.toISOString().slice(0, 10))}%`, top: 0, bottom: 0, borderLeft: '1px solid var(--border)', paddingLeft: 4, fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                  {tk.label}
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          {dated.map((r) => {
            const left = axis.pct(r.engagementDate); const right = axis.pct(r.pogDate);
            const w = Math.max(0.6, right - left);
            const col = customerColor(r.customer);
            const dim = r.status === 'Completed' || r.status === 'Cancelled' ? 0.45 : 0.9;
            const dec = axis.pct(r.decisionDate); const dc = axis.pct(r.dcDate);
            return (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--bg-elevated)', minHeight: 26 }}>
                <div style={{ width: 230, minWidth: 230, padding: '4px 10px 4px 12px', borderRight: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6, borderLeft: `3px solid ${col}` }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={`${r.customer} — ${r.subCategory}`}>{r.subCategory}</span>
                  <span style={{ fontSize: 9, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{r.customer}</span>
                </div>
                <div style={{ position: 'relative', flex: 1, height: 26 }}
                  title={`${r.customer} · ${r.subCategory} (${r.type}, ${r.status})\nEngagement ${r.engagementDate}\nDecision ${r.decisionDate || '—'}\nDC ${r.dcDate || '—'}\nPOG ${r.pogDate}${r.rangeReviewDate ? `\nRange Review ${r.rangeReviewDate}` : ''}`}>
                  {/* gridlines */}
                  {axis.ticks.map((tk, i) => (
                    <div key={i} style={{ position: 'absolute', left: `${axis.pct(tk.date.toISOString().slice(0, 10))}%`, top: 0, bottom: 0, borderLeft: '1px solid var(--bg-elevated)' }} />
                  ))}
                  {axis.todayPct != null && <div style={{ position: 'absolute', left: `${axis.todayPct}%`, top: 0, bottom: 0, width: 1.5, background: '#6366f1', opacity: 0.7 }} />}
                  {/* bar */}
                  <div style={{ position: 'absolute', left: `${left}%`, width: `${w}%`, top: '50%', transform: 'translateY(-50%)', height: 12, background: `color-mix(in srgb, ${col} 26%, transparent)`, border: `1px solid color-mix(in srgb, ${col} 60%, transparent)`, borderRadius: 3, opacity: dim }} />
                  {/* milestone markers */}
                  {dec != null && <Marker left={dec} color={col} />}
                  {dc != null && <Marker left={dc} color={col} dark />}
                  {/* POG cap */}
                  {right != null && <div style={{ position: 'absolute', left: `${right}%`, top: '50%', transform: 'translate(-50%,-50%)', width: 7, height: 7, background: col, borderRadius: '50%', opacity: dim }} />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Marker({ left, color, dark }) {
  return <div style={{ position: 'absolute', left: `${left}%`, top: '50%', transform: 'translate(-50%,-50%) rotate(45deg)', width: 5, height: 5, background: dark ? `color-mix(in srgb, ${color} 70%, black)` : color, opacity: 0.9 }} />;
}

function Legend({ dot, marker, dark, text }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {marker
        ? <span style={{ width: 5, height: 5, background: dark ? '#334155' : '#64748b', transform: 'rotate(45deg)', display: 'inline-block' }} />
        : <span style={{ width: 12, height: 6, background: 'color-mix(in srgb, #64748b 26%, transparent)', border: '1px solid color-mix(in srgb, #64748b 60%, transparent)', borderRadius: 2, display: 'inline-block' }} />}
      {text}
    </span>
  );
}
